import { supabase } from "@/lib/supabase";

export interface OrganizationsListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  onboarding?: string;
  createdAfter?: string;
  sortField?: string;
  sortDirection?: "asc" | "desc";
}

export interface Organization {
  id: string;
  name: string;
  logo_url?: string;
  brand_color?: string;
  created_at: string;
  updated_at: string;
  website?: string;
  subscription_status?: string;
  plan_name?: string;
  active_agents_count?: number;
  ai_assistant_instances_count?: number;
  ai_outputs_count?: number;
  is_test_org: boolean;
  onboarding_meta?: any;
  profile_email?: string;
  profile_full_name?: string;
  industry_name?: string;
  industry_emoji?: string;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface OrganizationsListResponse {
  success: boolean;
  data: Organization[];
  pagination: PaginationInfo;
}

export interface DashboardMetrics {
  new_signups_today: number;
  new_signups_yesterday: number;
  new_signups_last7: number;
  completed_today: number;
  completed_yesterday: number;
  completed_last7: number;
  outputs_today: number;
  outputs_yesterday: number;
  outputs_last7: number;
}

/**
 * Admin Organizations Service
 * Communicates with Express API for admin operations
 */
export const adminOrganizationsService = {
  /**
   * Get paginated, filtered, and sorted list of organizations
   * Uses Express API with server-side pagination
   */
  async getOrganizations(
    params: OrganizationsListParams,
  ): Promise<OrganizationsListResponse> {
    const { data, error } = await supabase.functions.invoke(
      "admin-organizations",
      {
        body: {
          action: "list",
          ...params,
        },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to fetch organizations");
    }

    return data;
  },

  /**
   * Get dashboard metrics
   * Heavily cached on server-side
   */
  async getMetrics(): Promise<DashboardMetrics> {
    const { data, error } = await supabase.functions.invoke(
      "admin-organizations",
      {
        body: {
          action: "metrics",
        },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to fetch metrics");
    }

    return data;
  },

  /**
   * Get single organization by ID
   */
  async getOrganizationById(id: string): Promise<Organization> {
    const { data, error } = await supabase.functions.invoke(
      "admin-organizations",
      {
        body: {
          action: "getById",
          id,
        },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to fetch organization");
    }

    return data.data;
  },

  /**
   * Update organization
   */
  async updateOrganization(
    id: string,
    updates: Partial<Pick<Organization, "is_test_org" | "subscription_status">>,
  ): Promise<Organization> {
    const { data, error } = await supabase.functions.invoke(
      "admin-organizations",
      {
        body: {
          action: "update",
          id,
          updates,
        },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to update organization");
    }

    return data.data;
  },

  /**
   * Change organization subscription plan
   */
  async changePlan(params: {
    organizationId: string;
    planId: string;
    applyImmediately?: boolean;
    preview?: boolean;
  }): Promise<any> {
    const { data, error } = await supabase.functions.invoke(
      "admin-organizations",
      {
        body: {
          action: "changePlan",
          organizationId: params.organizationId,
          planId: params.planId,
          applyImmediately: params.applyImmediately ?? true,
          preview: params.preview ?? false,
        },
      },
    );

    if (error) {
      throw new Error(error.message || "Failed to change plan");
    }

    return data;
  },
};
